import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../lib/PlayerContext';
import { Song, Playlist, Artist } from '../types/models';
import { fetchAllData, favorites, fetchPlaylists, fetchAlbums } from '../lib/api';
import { storage } from '../lib/api';
import { SongCard } from '../components/SongCard';
import CreatePlaylistModal from '../components/CreatePlaylistModal';
import { getArtistNames } from '../utils/artistUtils';

const Library: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const player = usePlayer();
  const [favoriteSongs, setFavoriteSongs] = useState<Song[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [albums, setAlbums] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'songs' | 'playlists' | 'albums'>('all');
  const [isCreatePlaylistModalOpen, setIsCreatePlaylistModalOpen] = useState(false);

  // Store the full data including artists and voicebanks
  const [artistsMap, setArtistsMap] = useState<Record<string, string>>({});
  const [allArtists, setAllArtists] = useState<Artist[]>([]);
  const [voicebanksMap, setVoicebanksMap] = useState<Record<string, string>>({});

  useEffect(() => {
    async function loadLibraryData() {
      try {
        setLoading(true);
        setError(null);
        
        // Load all data to get artists and voicebanks info
        const allData = await fetchAllData();
        
        // Create maps for quick lookups
        const artistsLookup: Record<string, string> = {};
        const voicebanksLookup: Record<string, string> = {};
        
        allData.artists.forEach(artist => {
          artistsLookup[artist.id] = artist.name;
        });
        
        allData.voicebanks.forEach(voicebank => {
          voicebanksLookup[voicebank.id] = voicebank.name;
        });
        
        setArtistsMap(artistsLookup);
        setAllArtists(allData.artists);
        setVoicebanksMap(voicebanksLookup);

        if (user) {
          // User is logged in, load data from API
          try {
            // Load favorites with fallback
            try {
              const favoritesData = await favorites.getFavorites();
              setFavoriteSongs(favoritesData);
            } catch (favoritesError) {
              console.error('Error fetching favorites:', favoritesError);
              // Fallback to empty array if API fails
              setFavoriteSongs([]);
            }
            
            // Load playlists with fallback
            try {
              const playlistsData = await fetchPlaylists();
              setPlaylists(playlistsData);
            } catch (playlistsError) {
              console.error('Error fetching playlists:', playlistsError);
              // Fallback to empty array if API fails
              setPlaylists([]);
            }
            
            // Load albums with fallback
            try {
              const albumsData = await fetchAlbums();
              setAlbums(albumsData);
            } catch (albumsError) {
              console.error('Error fetching albums:', albumsError);
              // Fallback to empty array if API fails
              setAlbums([]);
            }
          } catch (apiError) {
            console.error('Error fetching library data from API:', apiError);
            setError('Some library data could not be loaded. You can still browse available content.');
          }
        } else {
          // Guest user, use local storage
          const favoriteIds = storage.getFavorites();
          const localFavorites = allData.songs.filter(song => favoriteIds.includes(song.id));
          setFavoriteSongs(localFavorites);
          
          // For guests, we don't show playlists
          setPlaylists([]);
        }
      } catch (error) {
        console.error('Error loading library data:', error);
        setError('Something went wrong while loading your library.');
      } finally {
        setLoading(false);
      }
    }
    
    loadLibraryData();
  }, [user]);

  // Play a song
  const playSong = (song: Song) => {
    player.playSong(song);
  };

  // Play all songs in the library
  const playAllSongs = () => {
    if (favoriteSongs.length > 0) {
      player.playSong(favoriteSongs[0], favoriteSongs);
    }
  };

  const removeFromLibrary = async (songId: string) => {
    try {
      setFavoriteSongs(prev => prev.filter(song => song.id !== songId));
      
      if (user) {
        // Remove from API if user is logged in
        await favorites.removeFavorite(songId);
      } else {
        // Remove from local storage if guest
        const favoriteIds = storage.getFavorites();
        storage.setFavorites(favoriteIds.filter(id => id !== songId));
      }
    } catch (error) {
      console.error('Error removing from library:', error);
      // Reload favorites to restore state
      const data = await fetchAllData();
      const favoriteIds = user 
        ? (await favorites.getFavorites()).map(song => song.id)
        : storage.getFavorites();
      setFavoriteSongs(data.songs.filter(song => favoriteIds.includes(song.id)));
    }
  };

  const handlePlaylistCreated = async () => {
    // Reload playlists after creating a new one
    try {
      const playlistsData = await fetchPlaylists();
      setPlaylists(playlistsData);
    } catch (error) {
      console.error('Error reloading playlists:', error);
    }
  };
  
  // Render different content based on active tab
  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-naia-pink"></div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="text-center py-16">
          <p className="text-xl text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-naia-pink text-pearl-white rounded hover:bg-opacity-80 transition"
          >
            Try Again
          </button>
        </div>
      );
    }
    
    // All tab shows everything
    if (activeTab === 'all') {
      if (favoriteSongs.length === 0 && playlists.length === 0 && albums.length === 0) {
        return (
          <div className="text-center py-16">
            <p className="text-xl text-slate-grey mb-4">Your library is empty</p>
            <p className="text-slate-grey">Add songs, create playlists, or explore albums to build your library</p>
          </div>
        );
      }
      
      return (
        <div className="space-y-8">
          {favoriteSongs.length > 0 && (
            <div>
              <h2 className="text-2xl font-outfit font-bold text-white mb-4">Songs</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {favoriteSongs.slice(0, 8).map(song => (
                  <div key={song.id} className="relative group">
                    <div onClick={() => playSong(song)} className="cursor-pointer">
                      <SongCard
                        song={song}
                        artist={getArtistNames(song.artistId, allArtists)}
                        voicebank={voicebanksMap[song.synthId] || 'Unknown Voicebank'}
                      />
                    </div>
                    <button
                      onClick={() => removeFromLibrary(song.id)}
                      className="absolute -top-3 -right-3 z-10 bg-naia-pink text-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      title="Remove from library"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              {favoriteSongs.length > 8 && (
                <div className="text-center mt-4">
                  <button 
                    onClick={() => setActiveTab('songs')}
                    className="text-naia-pink hover:underline"
                  >
                    View all songs
                  </button>
                </div>
              )}
            </div>
          )}
          
          {playlists.length > 0 && (
            <div>
              <h2 className="text-2xl font-outfit font-bold text-white mb-4">Playlists</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {playlists.slice(0, 4).map(playlist => (
                  <Link 
                    key={playlist.id} 
                    to={`/playlist/${playlist.id}`}
                    className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors"
                  >
                    <div className="p-4">
                      <h3 className="text-lg font-medium text-white truncate">{playlist.name}</h3>
                      <p className="text-gray-400 text-sm mt-1">{playlist.songCount || 0} songs</p>
                    </div>
                  </Link>
                ))}
              </div>
              {playlists.length > 4 && (
                <div className="text-center mt-4">
                  <button 
                    onClick={() => setActiveTab('playlists')}
                    className="text-naia-pink hover:underline"
                  >
                    View all playlists
                  </button>
                </div>
              )}
            </div>
          )}
          
          {albums.length > 0 && (
            <div>
              <h2 className="text-2xl font-outfit font-bold text-white mb-4">Albums</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {albums.slice(0, 4).map(album => (
                  <Link 
                    key={album.id} 
                    to={`/album/${album.id}`}
                    className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors"
                  >
                    {album.coverUrl && (
                      <img 
                        src={album.coverUrl} 
                        alt={album.title} 
                        className="w-full aspect-square object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h3 className="text-lg font-medium text-white truncate">{album.title}</h3>
                      <p className="text-gray-400 text-sm mt-1">{artistsMap[album.artistId] || 'Various Artists'}</p>
                    </div>
                  </Link>
                ))}
              </div>
              {albums.length > 4 && (
                <div className="text-center mt-4">
                  <button 
                    onClick={() => setActiveTab('albums')}
                    className="text-naia-pink hover:underline"
                  >
                    View all albums
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Songs tab
    if (activeTab === 'songs') {
      if (favoriteSongs.length === 0) {
        return (
          <div className="text-center py-16">
            <p className="text-xl text-slate-grey mb-4">No songs in your library</p>
            <p className="text-slate-grey">Add songs to your library to see them here</p>
          </div>
        );
      }
      
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {favoriteSongs.map(song => (
            <div key={song.id} className="relative group">
              <div onClick={() => playSong(song)} className="cursor-pointer">
                <SongCard
                  song={song}
                  artist={getArtistNames(song.artistId, allArtists)}
                  voicebank={voicebanksMap[song.synthId] || 'Unknown Voicebank'}
                />
              </div>
              <button
                onClick={() => removeFromLibrary(song.id)}
                className="absolute -top-3 -right-3 z-10 bg-naia-pink text-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                title="Remove from library"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      );
    }
    
    // Playlists tab
    if (activeTab === 'playlists') {
      if (playlists.length === 0) {
        return (
          <div className="text-center py-16">
            <p className="text-xl text-slate-grey mb-4">No playlists yet</p>
            <p className="text-slate-grey mb-4">Create playlists to organize your favorite songs</p>
            <button
              onClick={() => setIsCreatePlaylistModalOpen(true)}
              className="px-4 py-2 bg-naia-pink text-pearl-white rounded hover:bg-opacity-80 transition"
            >
              Create Playlist
            </button>
          </div>
        );
      }
      
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {playlists.map(playlist => (
            <Link 
              key={playlist.id} 
              to={`/playlist/${playlist.id}`}
              className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors"
            >
              <div className="p-4">
                <h3 className="text-lg font-medium text-white truncate">{playlist.name}</h3>
                <p className="text-gray-400 text-sm mt-1">{playlist.songCount || 0} songs</p>
              </div>
            </Link>
          ))}
          <div 
            onClick={() => setIsCreatePlaylistModalOpen(true)}
            className="border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center h-48 cursor-pointer hover:border-naia-pink transition-colors"
          >
            <div className="text-center">
              <div className="text-3xl text-gray-500 mb-2">+</div>
              <p className="text-gray-400">Create Playlist</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Albums tab
    if (activeTab === 'albums') {
      if (albums.length === 0) {
        return (
          <div className="text-center py-16">
            <p className="text-xl text-slate-grey mb-4">No albums in your library</p>
            <p className="text-slate-grey">Save albums to your library to see them here</p>
          </div>
        );
      }
      
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {albums.map(album => (
            <Link 
              key={album.id} 
              to={`/album/${album.id}`}
              className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors"
            >
              {album.coverUrl && (
                <img 
                  src={album.coverUrl} 
                  alt={album.title} 
                  className="w-full aspect-square object-cover"
                />
              )}
              <div className="p-4">
                <h3 className="text-lg font-medium text-white truncate">{album.title}</h3>
                <p className="text-gray-400 text-sm mt-1">{artistsMap[album.artistId] || 'Various Artists'}</p>
              </div>
            </Link>
          ))}
        </div>
      );
    }
    
    return null;
  };

  // If not authenticated, redirect to home
  if (!authLoading && !user) {
    return <Navigate to="/" />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-outfit font-bold text-white">Your Library</h1>
        
        {favoriteSongs.length > 0 && (
          <button 
            onClick={playAllSongs}
            className="bg-naia-pink hover:bg-opacity-80 text-white px-4 py-2 rounded transition-colors"
          >
            Play All
          </button>
        )}
      </div>
      
      {/* Library tabs */}
      <div className="border-b border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('all')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'all' 
              ? 'border-naia-pink text-naia-pink' 
              : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'} transition-colors`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('songs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'songs' 
              ? 'border-naia-pink text-naia-pink' 
              : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'} transition-colors`}
          >
            Songs
          </button>
          <button
            onClick={() => setActiveTab('playlists')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'playlists' 
              ? 'border-naia-pink text-naia-pink' 
              : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'} transition-colors`}
          >
            Playlists
          </button>
          <button
            onClick={() => setActiveTab('albums')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'albums' 
              ? 'border-naia-pink text-naia-pink' 
              : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'} transition-colors`}
          >
            Albums
          </button>
        </nav>
      </div>

      {renderTabContent()}

      {/* Create Playlist Modal */}
      <CreatePlaylistModal
        isOpen={isCreatePlaylistModalOpen}
        onClose={() => setIsCreatePlaylistModalOpen(false)}
        onPlaylistCreated={handlePlaylistCreated}
      />
    </div>
  );
};

export default Library;
